import React, { useState, useMemo, useEffect } from "react";
import api from "../../../config/axiosConfig.js";
import EditableInvoiceTable from "../EditableInvoiceTable.js";
import Select from "react-select";
import Swal from "sweetalert2";

import useDteJson from "./hook_dte/useDteJson.js";
import { useUpdateComprobante, useComprobantes } from "../../../hooks_cache/useComprobantes.js";
import usePopupFactura from "./hook_dte/usePopupFactura";
import useModalProgreso from "../../../components/hooks/useModalProgreso.js";
import ResumenDteView from "./ResumenDteView.js";

import useResumenFE from "./useResumenFE.js";
import useResumenCCF from "./useResumenCCF.js";
import useResumenFEX from "./useResumenFEX.js";
import useResumenNCE from "./useResumenNCE.js";

import { z } from "zod";

const hookMap = {
  "01": {
    endpoint: "/dte/procesarFE",
    useHook: useResumenFE,
  },
  "03": {
    endpoint: "/dte/procesarCCFE",
    useHook: useResumenCCF,
  },
  "05": {
    endpoint: "/dte/procesarNCE",
    useHook: useResumenNCE,
  },
  11: {
    endpoint: "/dte/procesarFEX",
    useHook: useResumenFEX,
  },
};

const DteFormFactory = ({ emisor, tercero, tipoDteSelected, itemsPrecargados, isTerceroOk }) => {
  const { data: comprobantes } = useComprobantes();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const codigoTipoDte = tipoDteSelected?.codigo;
  const tipoDteId = tipoDteSelected?.id;

  const [comprobanteRelacionado, setComprobanteRelacionado] = useState(null);
  const [facturaItems, setFacturaItems] = useState([]);
  const { factura, setReceptor, setEmisor, setItems, setResumen, setDocumentoRelacionado, itemBase, formatearItem, buildJson } =
    useDteJson(codigoTipoDte);
  const updateComprobanteCache = useUpdateComprobante();
  const { abrirPopupFacturaByComprobanteId, abrirPopupFacturaByHtml } = usePopupFactura();

  const { start, stop } = useModalProgreso({
    modalTitle: "Registrando comprobante...",
    socketEvents: {
      progress: "dte:progreso",
      error: "dte:error",
    },
  });

  const [formaPago, setFormaPago] = useState({
    value: 2,
    label: "CREDITO",
    diascredito: tercero?.diascredito || 0,
  });

  const { calcularFacturaResumen } = hookMap[codigoTipoDte].useHook({
    setFacturaItems,
    facturaItems,
    tercero,
    itemsPrecargados,
    setResumen,
    formaPago,
  });

  useEffect(() => {
    if (comprobanteRelacionado) {
      setDocumentoRelacionado(comprobanteRelacionado);
    }
  }, [comprobanteRelacionado]);

  useEffect(() => {
    setEmisor(emisor);
  }, [emisor, tipoDteSelected]);

  useEffect(() => {
    if (!tercero) return;
    calcularFacturaResumen();
  }, [facturaItems, tercero, tipoDteSelected]);

  useEffect(() => {
    if (tercero) {
      setReceptor(tercero);
      setFormaPago((prev) => ({ ...prev, diascredito: tercero.diascredito || 0 }));
      setComprobanteRelacionado(null);
    }
  }, [tercero, tipoDteSelected]);

  useEffect(() => {
    setItems(facturaItems);
  }, [facturaItems]);

  useEffect(() => {
    if (facturaItems.length > 0) {
      setFacturaItems([]);
    }
  }, [tipoDteSelected]);

  const handleRegistrarComprobante = async () => {
    if (isSubmitting) return; // Evita múltiples clics
    setIsSubmitting(true);

    start();
    try {
      const factura = buildJson();

      const response = await api.post(hookMap[codigoTipoDte].endpoint, {
        dte: factura,
        id_empresa: emisor.id,
        id_tercero: tercero.id,
        id_tipoDTE: tipoDteId,
        comprobanteRelacionado,
      });

      const { dte, insertResponse } = response.data;

      updateComprobanteCache(insertResponse);
      setFacturaItems([]);
      stop();
      Swal.fire({
        title: "Comprobante registrado con éxito!",
        html: `
          <b>Fecha de emisión:</b> ${dte.identificacion.fecEmi}<br/>
          <b>Código de generación:</b> ${dte.identificacion.codigoGeneracion}<br/>
          <b>Número de control:</b> ${dte.identificacion.numeroControl}<br/>
          <b>Sello recibido:</b> ${dte.selloRecibido}<br/>
        `,
        icon: "success",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Imprimir",
        denyButtonText: "Imprimir y Enviar",
        cancelButtonText: "Cerrar",
      }).then((result) => {
        if (result.isConfirmed || result.isDenied) {
          abrirPopupFacturaByComprobanteId(insertResponse.id, result.isDenied);
        }
      });
    } catch (error) {
      stop();
      console.error("❌ Error al registrar comprobante:", error);
      setTimeout(() => {
        Swal.fire({
          title: "Error",
          html: `<pre style="text-align: left; white-space: pre-wrap;">${formatearError(error)}</pre>`,
          icon: "error",
          customClass: {
            popup: "text-start",
          },
        });
      }, 100); // 100ms suele ser suficiente
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatearError = (error) => {
    if (!error) return "❌ Error desconocido";

    if (error instanceof z.ZodError) {
      return error.errors.map((e) => `• ${e.path.join(".") || "general"}: ${e.message}`).join("\n");
    }

    // Si es string plano
    if (typeof error === "string") return `❌ ${error}`;

    // Si error es un objeto con un campo `error` que contiene la estructura del MH
    const err = error?.error || error;

    // Caso con observaciones del MH
    if (err?.observaciones?.length) {
      return `🚫 ${err.descripcionMsg || error.mensaje || "Error en el documento"}\n\n- ${err.observaciones.join("\n- ")}`;
    }

    // Caso solo con descripcionMsg
    if (err?.descripcionMsg) {
      return `🚫 ${err.descripcionMsg}`;
    }

    // Caso común con mensaje y error como texto
    if (error?.mensaje || err?.message) {
      return `❌ ${error.mensaje || err.message}`;
    }

    // Fallback
    return JSON.stringify(error, null, 2);
  };

  const customComprobantes = useMemo(
    () =>
      comprobantes
        ?.filter((t) => t.Tercero?.id === tercero?.id) // Filtramos por id_tercero
        ?.filter((t) => t.TipoDTE.codigo === "03") // Filtramos por tipo de DTE
        .map((t) => ({
          ...t,
          value: t.id,
          label: `${t.fecEmi} | ${t.numeroControl} | ${t.codigoGeneracion}`,
        })) || [],
    [comprobantes, tercero]
  );

  const handleVistaPreviaDte = async () => {
    if (isSubmitting) return; // Evita múltiples clics
    setIsSubmitting(true);
    start();
    try {
      const factura = buildJson();
      const response = await api.post("dte/vistaprevia", {
        dte: factura,
        id_empresa: emisor.id,
        tipoDte: { codigo: codigoTipoDte, descripcion: tipoDteSelected?.descripcion },
        comprobanteRelacionado,
      });

      abrirPopupFacturaByHtml(response.data.html);
      stop();
    } catch (error) {
      stop();
      console.error("❌ Error al mostrar vista previa de comprobante:", error);
      setTimeout(() => {
        Swal.fire({
          title: "Error",
          html: `<pre style="text-align: left; white-space: pre-wrap;">${formatearError(error)}</pre>`,
          icon: "error",
          customClass: {
            popup: "text-start",
          },
        });
      }, 100); // 100ms suele ser suficiente
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {codigoTipoDte !== "05" && (
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-md-2">
            <label className="form-label">Forma de pago:</label>

            <Select
              value={formaPago}
              options={[
                { value: 2, label: "CREDITO", diascredito: tercero?.diascredito || 0 },
                { value: 1, label: "CONTADO", diascredito: 0 },
              ]}
              isSearchable
              placeholder="Forma de pago"
              onChange={(selected) => setFormaPago(selected)}
            />
          </div>

          {formaPago.value === 2 && (
            <div className="col-6 col-md-1">
              <label className="form-label">Dias de credito:</label>
              <input
                type="number"
                className="form-control"
                value={formaPago.diascredito}
                onChange={(e) => setFormaPago((prev) => ({ ...prev, diascredito: e.target.value }))}
              />
            </div>
          )}
        </div>
      )}

      {codigoTipoDte === "05" && (
        <div className="row">
          <div className="col-md-7">
            <Select
              value={comprobanteRelacionado}
              options={customComprobantes}
              isSearchable
              placeholder="Seleccione comprobante a relacionar"
              onChange={(selected) => setComprobanteRelacionado(selected)}
            />
          </div>
        </div>
      )}

      <hr />

      <EditableInvoiceTable
        data={facturaItems}
        setData={setFacturaItems}
        tipoFactura={codigoTipoDte}
        comprobanteRelacionado={comprobanteRelacionado}
        itemBase={itemBase}
        formatearItem={formatearItem}
      />

      <ResumenDteView factura={factura} />
      <hr />

      <div className="row text-center">
        <div className="col-md-12">
          <button
            className="btn btn-blue me-2"
            onClick={() => handleVistaPreviaDte()}
            disabled={facturaItems.length === 0 || !tercero || !isTerceroOk || isSubmitting}
          >
            <span className="d-flex align-items-center text-start">
              <i className="fas fa-print fa-2x me-2 text-black"></i>
              <span>
                <span className="d-block">Vista previa</span>
              </span>
            </span>
          </button>

          <button
            className="btn btn-green"
            onClick={() => handleRegistrarComprobante()}
            disabled={facturaItems.length === 0 || !tercero || !isTerceroOk || isSubmitting}
          >
            <span className="d-flex align-items-center text-start">
              <i className="fas fa-print fa-2x me-2 text-black"></i>
              <span>
                <span className="d-block">Registrar comprobante</span>
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DteFormFactory;
